import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2,
  Star,
  CalendarDays,
  PawPrint,
  Dog,
  Activity,
  Home,
  GraduationCap,
  Heart,
  Send,
} from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useBookingPopup } from "@/hooks/use-booking-popup";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import BlogSection from "@/components/BlogSection";
import TrustBar from "@/components/TrustBar";
import AnimatedSection from "@/components/AnimatedSection";
import ReviewsSection from "@/components/ReviewsSection";
import SocialMediaSection from "@/components/SocialMediaSection";
import AccreditationBadges from "@/components/AccreditationBadges";
import SEOHead from "@/components/SEOHead";

const Index = () => {
  const { openPopup } = useBookingPopup();
  const [submitted, setSubmitted] = useState(false);

  const services = [
    {
      title: "Puppy Training",
      description:
        "We're here to help you build the best start for your new best friend.",
      icon: <Dog className="w-10 h-10 text-primary" strokeWidth={1.5} />,
      cta: "START PUPPY TRAINING",
      link: "/puppy-training",
    },
    {
      title: "1:1 Dog Training\nand Behaviour",
      description:
        "Practical, kind support for behavioural challenges to help your dog feel calmer and you feel more confident.",
      icon: <Activity className="w-10 h-10 text-primary" strokeWidth={1.5} />,
      cta: "BOOK 1:1 SUPPORT",
      link: "/1-1-training",
    },
    {
      title: "Dog Training\nGroup Classes",
      description:
        "Fun, supportive classes for real-world skills, confidence and better behaviour.",
      icon: (
        <GraduationCap className="w-10 h-10 text-primary" strokeWidth={1.5} />
      ),
      cta: "VIEW CLASS SCHEDULE",
      link: "/classes",
    },
    {
      title: "Dog Walking",
      description:
        "Reliable, trusted dog walking to keep your dog happy, active and enriched.",
      icon: <PawPrint className="w-10 h-10 text-primary" strokeWidth={1.5} />,
      cta: "ARRANGE A DOG WALK",
      link: "/walking",
    },
    {
      title: "Overnight\nPet Sits",
      description:
        "Peace of mind while you're away. Your pets stay happy, comfortable and cared for in their own home.",
      icon: <Home className="w-10 h-10 text-primary" strokeWidth={1.5} />,
      cta: "CHECK PET SIT AVAILABILITY",
      link: "/pet-sits",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Home"
        description="Modern, ethical dog training and behaviour support in Nottinghamshire. No Judgement. Just Support."
        canonical="/"
        keywords="Dog training Nottinghamshire, puppy training, behaviour support, WagRight, ethical dog training"
        ogImage="https://vibe.filesafe.space/1780604500184904220/attachments/b7e211db-1771-433b-bb08-2531b620741c.jpg"
      />
      {/* Pinned Section (Trust Bar + Hero) */}
      <TrustBar>
        {/* Hero Section */}
        <div className="relative bg-primary flex flex-col lg:block lg:min-h-[600px] overflow-hidden font-body z-10">
          <div className="hidden lg:block relative w-full lg:absolute lg:inset-0 lg:left-1/3 lg:h-full z-0 shrink-0">
            <img
              src="https://vibe.filesafe.space/1780604500184904220/attachments/b7e211db-1771-433b-bb08-2531b620741c.jpg"
              alt="Dog Training"
              className="w-full h-full object-cover object-center"
              fetchPriority="high"
              decoding="async"
            />
          </div>
          <div
            className="hidden lg:block absolute inset-0 bg-primary z-10"
            style={{ clipPath: "polygon(0 0, 65% 0, 45% 100%, 0% 100%)" }}
          ></div>

          <div className="container mx-auto px-4 relative z-20 py-12 md:py-16 lg:py-20 max-w-7xl lg:flex lg:items-center lg:min-h-[600px]">
            <div className="max-w-xl space-y-6">
              <div className="flex items-center gap-2 text-white mb-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                  ))}
                </div>
                <span className="font-medium">5 star reviews</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-display font-bold text-white leading-tight">
                Modern, ethical dog <br />
                training and behaviour <br />
                support in <span className="text-accent">Nottinghamshire.</span>
              </h1>
              <div className="text-2xl md:text-3xl font-display font-bold text-accent tracking-wide">
                No Judgement. Just Support.
              </div>
              <ul className="space-y-3 pt-4">
                {[
                  "Supportive, achievable, and rewarding",
                  "Calmer and more connected relationship with your dog",
                  "Modern science-based training",
                  "Practical real-life coaching",
                  "Kind, ethical support designed to build trust,\nconfidence, and lasting results",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white">
                    <div className="mt-1 bg-accent rounded-full p-0.5 shrink-0">
                      <CheckCircle2
                        className="w-4 h-4 text-white"
                        strokeWidth={3}
                      />
                    </div>
                    <span className="font-medium whitespace-pre-line">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-lg text-white font-medium pt-4">
                At WagRight, we believe dog training should feel supportive,
                achievable, and rewarding – for both you and your dog.
              </p>
              <div className="pt-4">
                <Button
                  onClick={openPopup}
                  size="lg"
                  className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-white font-bold px-4 sm:px-8 py-6 rounded-sm text-sm sm:text-lg uppercase tracking-wide whitespace-normal h-auto"
                >
                  CLAIM YOUR FREE ASSESSMENT
                </Button>
              </div>
            </div>
          </div>
        </div>
      </TrustBar>

      {/* Accreditation Badges */}
      <AccreditationBadges />

      {/* Services Section */}
      <section className="py-24 bg-[#fcfcfc] font-body">
        <AnimatedSection className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-16">
            <div className="h-px bg-accent w-8 sm:w-16 md:w-32 shrink-0"></div>
            <h2 className="flex items-center gap-2 text-primary font-display font-bold text-2xl sm:text-3xl md:text-4xl tracking-wide uppercase shrink-0 text-center">
              <PawPrint
                className="w-6 h-6 sm:w-8 sm:h-8 text-accent shrink-0"
                fill="currentColor"
              />
              <span>OUR SERVICES</span>
            </h2>
            <div className="h-px bg-accent w-8 sm:w-16 md:w-32 shrink-0"></div>
          </div>
          <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
            {services.map((service, idx) => (
              <Card
                key={idx}
                className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-white border-2 border-border/50 shadow-sm hover:shadow-md transition-shadow text-center flex flex-col items-center p-6 rounded-xl"
              >
                <div className="w-20 h-20 rounded-full border-2 border-primary flex items-center justify-center mb-4 text-primary">
                  {service.icon}
                </div>
                <h3 className="text-[15px] font-bold text-primary mb-3 min-h-[48px] flex items-center justify-center whitespace-pre-line">
                  {service.title}
                </h3>
                <p className="text-xs text-primary/80 mb-6 flex-grow font-medium leading-relaxed">
                  {service.description}
                </p>
                <Button
                  asChild
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs py-5 whitespace-pre-line leading-tight tracking-wider rounded-sm uppercase"
                >
                  <Link to={service.link}>{service.cta}</Link>
                </Button>
              </Card>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* Mailing List Section */}
      <section className="bg-primary py-24 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none overflow-hidden flex items-center justify-center md:justify-end">
          <img
            src="https://vibe.filesafe.space/1780604500184904220/attachments/966faee0-985d-4a17-9ffd-2d5ba45ce4bd.png"
            alt=""
            className="w-auto h-[120%] md:h-[160%] object-contain opacity-50 md:translate-x-1/4"
            loading="lazy"
            decoding="async"
          />
        </div>
        <AnimatedSection className="container mx-auto px-4 max-w-4xl relative z-10 text-center space-y-8">
          <div className="inline-block bg-accent px-4 py-1 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-4">
            STAY CONNECTED
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold uppercase tracking-wide leading-tight">
            Sign up to our mailing list
          </h2>
          <p className="text-white/80 text-xl font-medium max-w-2xl mx-auto">
            Helping dogs and their humans thrive together. Get expert tips,
            updates, and exclusive support delivered to your inbox.
          </p>
          <form
            className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const email = (formData.get("email") as string) || "";

              // Push lead to CRM
              fetch("https://backend.leadconnectorhq.com/vibe-ai/lead/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  locationId: "LybUg0eKBAfPYJVWJI73",
                  email,
                  type: "mailing_list_signup",
                }),
              }).catch(() => {});

              // Also fire tracking event
              fetch(
                "https://backend.leadconnectorhq.com/external-tracking/events",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    version: "2021-07-28",
                  },
                  body: JSON.stringify({
                    type: "external_form_submission",
                    timestamp: Date.now(),
                    formId: "Mailing List Form",
                    formData: { email },
                    formLabels: { email: "Email Address" },
                    url: window.location.href,
                    title: document.title,
                    path: window.location.pathname,
                    userAgent: navigator.userAgent,
                    trackingId: "tk_d46c24c393554163a1c490060dfe2722",
                    locationId: "LybUg0eKBAfPYJVWJI73",
                    sessionId: crypto.randomUUID(),
                    properties: {
                      deviceType: /Mobile|Android|iPhone/i.test(
                        navigator.userAgent,
                      )
                        ? "mobile"
                        : "desktop",
                    },
                  }),
                },
              ).catch(() => {});
              e.currentTarget.reset();
              setSubmitted(true);
              setTimeout(() => setSubmitted(false), 5000);
            }}
          >
            {submitted ? (
              <div className="flex items-center justify-center gap-3 py-4 text-accent">
                <CheckCircle2 className="w-6 h-6" />
                <span className="font-semibold text-lg">
                  Thank you! You're subscribed.
                </span>
              </div>
            ) : (
              <>
                <Input
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  className="bg-white text-primary h-14 px-6 rounded-sm flex-grow border-none focus-visible:ring-accent"
                  required
                />
                <Button
                  type="submit"
                  className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-white font-bold h-auto py-4 px-4 sm:px-10 uppercase tracking-wide rounded-sm shrink-0 whitespace-normal"
                >
                  GET EXPERT TRAINING TIPS{" "}
                  <Send className="w-4 h-4 ml-2 shrink-0" />
                </Button>
              </>
            )}
          </form>
        </AnimatedSection>
      </section>

      {/* About Section */}
      <section className="py-24 bg-[#f4f3f0] font-body">
        <AnimatedSection className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative rounded-tl-[100px] rounded-br-[100px] overflow-hidden aspect-[4/5] shadow-xl">
              <img
                src="https://vibe.filesafe.space/1780604500184904220/attachments/8ff1537f-c8e7-4ee3-87ba-bf785ef767f9.jpg"
                alt="About WagRight"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <PawPrint className="w-8 h-8 text-accent" fill="currentColor" />
                <h2 className="text-4xl font-display font-bold text-primary uppercase">
                  WHY CHOOSE WAGRIGHT?
                </h2>
              </div>
              <p className="font-script text-5xl text-accent -rotate-2 origin-left">
                At WagRight, we do things differently.
              </p>
              <div className="space-y-4 text-primary font-medium leading-relaxed">
                <p>
                  We understand that behind every barking dog, reactive dog,
                  anxious dog, or overwhelmed puppy is usually a human trying
                  their absolute best. That's why our approach is built around
                  kindness, understanding, and support — for both ends of the
                  lead.
                </p>
                <p>
                  What sets WagRight apart is the combination of modern
                  science-based training, real-life practical experience,
                  enrichment, scent work, and genuine emotional understanding.
                  We don't believe in harsh methods, unrealistic expectations,
                  or making owners feel judged for their dog's behaviour.
                </p>
                <p>
                  Instead, we focus on helping you understand your dog, build
                  trust, and create lasting change in a way that feels
                  achievable and supportive.
                </p>
              </div>
              <p className="font-script text-5xl text-accent py-4 -rotate-2 origin-left">
                Training should never feel intimidating.
              </p>
              <Button
                onClick={openPopup}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-sm tracking-wide uppercase"
              >
                START YOUR TRAINING JOURNEY
              </Button>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-primary text-white font-body">
        <AnimatedSection className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Heart
                  className="w-10 h-10 md:w-12 md:h-12 text-accent shrink-0"
                  strokeWidth={1.5}
                />
                <h2 className="text-3xl md:text-4xl font-display font-bold uppercase tracking-wide">
                  OUR MISSION
                </h2>
              </div>
              <p className="font-script text-4xl text-accent -rotate-2 origin-left">
                If WagRight could change the world, it would be this:
              </p>
              <div className="space-y-4 text-white/90 font-medium leading-relaxed text-sm md:text-base">
                <p>
                  Fewer dogs being surrendered, rehomed, or put to sleep simply
                  because their humans felt unsupported, overwhelmed, or afraid
                  to ask for help.
                </p>
                <p>
                  Our mission is to help people better understand their dogs
                  before situations reach crisis point. Through ethical
                  training, behaviour support, enrichment, and education, we aim
                  to strengthen the relationship between dogs and their humans
                  and keep more dogs happy, secure, and in loving homes where
                  they belong.
                </p>
              </div>
              <p className="font-script text-5xl text-accent pt-4 -rotate-2 origin-left font-display">
                No Judgement. Just Support.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden aspect-[4/3] shadow-2xl">
              <img
                src="https://vibe.filesafe.space/1780604500184904220/attachments/ce038b8f-df49-45dc-b85e-fef830b13f2f.jpg"
                alt="Mission"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      {/* CTA Section */}
      <section className="py-24 bg-primary text-white font-body relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none overflow-hidden flex items-center justify-center md:justify-start">
          <img
            src="https://vibe.filesafe.space/1780604500184904220/attachments/966faee0-985d-4a17-9ffd-2d5ba45ce4bd.png"
            alt=""
            className="w-auto h-[120%] md:h-[160%] object-contain opacity-50 md:-translate-x-1/4"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        <AnimatedSection className="container mx-auto px-4 max-w-4xl relative z-10 text-center space-y-8">
          <div className="flex justify-center mb-6">
            <CalendarDays
              className="w-12 h-12 md:w-16 md:h-16 text-accent"
              strokeWidth={1.5}
            />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold uppercase tracking-wide leading-tight">
            BOOK A FREE
            <br />
            ASSESSMENT CALL
          </h2>
          <p className="text-white/90 font-medium text-lg md:text-xl max-w-2xl mx-auto">
            Let's chat about your dog and how we can help you both thrive.
          </p>
          <div className="pt-4 px-4">
            <Button
              onClick={openPopup}
              size="lg"
              className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-white font-bold px-4 sm:px-12 py-6 sm:py-8 text-sm sm:text-lg rounded-sm tracking-wider uppercase whitespace-normal h-auto"
            >
              CLAIM YOUR FREE ASSESSMENT
            </Button>
          </div>
        </AnimatedSection>
      </section>

      {/* Blog Section */}
      <BlogSection />

      {/* Social Media Section */}
      <SocialMediaSection />
    </Layout>
  );
};

export default Index;
